import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { CheckCircle, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided');
      setLoading(false);
      return;
    }

    // Verify the invitation token
    const verifyToken = async () => {
      try {
        const response = await axios.get(`${API}/auth/verify-invite/${token}`);
        setUserInfo(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired invitation');
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  const isPasswordValid = () => {
    return (
      password.length >= 10 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /[0-9]/.test(password) &&
      /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(password) &&
      password === confirmPassword
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid()) {
      toast.error('Please ensure all password requirements are met');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/accept-invite`, {
        token,
        password
      });
      setSuccess(true);
      toast.success('Account activated successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate account');
    } finally {
      setSubmitting(false);
    }
  };

  const getRoleDisplay = (role, organization) => {
    switch (role) {
      case 'committee':
        return 'Group Member';
      case 'admin':
        return 'Administrator';
      case 'external_org':
        return `External Partner (${organization || 'N/A'})`;
      default:
        return role;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
            <p className="text-gray-600">Verifying invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <AlertCircle className="w-16 h-16 text-red-500" />
            </div>
            <CardTitle className="text-2xl text-red-600">Invitation Invalid</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-600">{error}</p>
            <p className="text-sm text-gray-500">
              Please contact your administrator at{' '}
              <a href="mailto:hello@holboroughlakes.uk" className="text-blue-600 hover:underline">
                hello@holboroughlakes.uk
              </a>{' '}
              to request a new invitation.
            </p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => navigate('/login')}
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle className="w-16 h-16 text-green-500" />
            </div>
            <CardTitle className="text-2xl text-green-600">Account Activated!</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-600">
              Your account has been successfully activated. You can now log in with your username and password.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-500 mb-1">Your username:</p>
              <p className="font-mono font-medium text-gray-800">{userInfo?.username}</p>
            </div>
            <Button 
              className="w-full bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600"
              onClick={() => navigate('/login')}
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_action-portal/artifacts/0jx7uwn8_HLRA_logo_non_transparent.jpeg" 
              alt="HLRA Logo" 
              className="h-20 w-auto"
            />
          </div>
          <CardTitle className="text-2xl">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-600">
              Welcome to HLRA Action Portal
            </span>
          </CardTitle>
          <CardDescription>Set your password to activate your account</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* User Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <p className="text-gray-500">Name</p>
                <p className="font-medium">{userInfo?.name}</p>
              </div>
              <div>
                <p className="text-gray-500">Username</p>
                <p className="font-medium font-mono">{userInfo?.username}</p>
              </div>
              <div>
                <p className="text-gray-500">Email</p>
                <p className="font-medium">{userInfo?.email}</p>
              </div>
              <div>
                <p className="text-gray-500">Role</p>
                <p className="font-medium">{getRoleDisplay(userInfo?.role, userInfo?.organization)}</p>
              </div>
            </div>
          </div>

          {/* Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Create Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Minimum 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="h-11 pr-10"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="h-11 pr-10"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Password requirements */}
            <div className="text-xs space-y-1 bg-gray-50 p-3 rounded-lg">
              <p className="font-medium text-gray-700 mb-2">Password requirements:</p>
              <p className={password.length >= 10 ? 'text-green-600' : 'text-gray-500'}>
                {password.length >= 10 ? '✓' : '○'} At least 10 characters
              </p>
              <p className={/[A-Z]/.test(password) ? 'text-green-600' : 'text-gray-500'}>
                {/[A-Z]/.test(password) ? '✓' : '○'} At least 1 uppercase letter (A-Z)
              </p>
              <p className={/[a-z]/.test(password) ? 'text-green-600' : 'text-gray-500'}>
                {/[a-z]/.test(password) ? '✓' : '○'} At least 1 lowercase letter (a-z)
              </p>
              <p className={/[0-9]/.test(password) ? 'text-green-600' : 'text-gray-500'}>
                {/[0-9]/.test(password) ? '✓' : '○'} At least 1 number (0-9)
              </p>
              <p className={/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(password) ? 'text-green-600' : 'text-gray-500'}>
                {/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(password) ? '✓' : '○'} At least 1 special character (!@#$% etc.)
              </p>
              <p className={password && confirmPassword && password === confirmPassword ? 'text-green-600' : 'text-gray-500'}>
                {password && confirmPassword && password === confirmPassword ? '✓' : '○'} Passwords match
              </p>
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600"
              disabled={submitting || !isPasswordValid()}
              data-testid="activate-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Activating...
                </>
              ) : (
                'Activate Account'
              )}
            </Button>
          </form>

          <div className="text-center text-xs text-gray-400 pt-4 border-t">
            <p>Already have an account?{' '}
              <Link to="/login" className="text-blue-600 hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
